import {
  Modal,
  ButtonGroup,
  Button,
  Radio,
  RadioGroup,
  TextField,
} from '@nike/eds';
import AsyncSelect from 'react-select/async';
import { MultiValue } from 'react-select';
import { useState, useEffect } from 'react';
import { useDebouncedCallback, useDebounce } from 'use-debounce';
import { useLoaderData } from 'react-router-dom';
import * as R from 'ramda';
import { useAtom } from 'jotai';
import { rankingRuleTriggerModalAtom } from '../../atoms/ranking';
import RecommendConceptsClient from '../../clientAPI/recommendConcepts';
import { RANKING_RULE_TRIGER_MODE } from 'constants/index';
import { isExistScope } from '../../utils/authHelpers';

export default function RankingRuleTriggerModal() {
  const loaderData = useLoaderData() as { access_token: string };
  const [triggerModal, setTriggerModal] = useAtom(rankingRuleTriggerModalAtom);
  const jwt = loaderData.access_token;
  const canWrite = isExistScope('search360:ranking:write', jwt);
  const [conceptMode, setConceptMode] = useState(
    RANKING_RULE_TRIGER_MODE.CASCADE,
  );
  const [selectedConcepts, setSelectedConcepts] = useState<
    RankingRuleTriggerConcept[]
  >([]);
  const [keywordType, setKeywordType] = useState(
    RANKING_RULE_TRIGER_MODE.EXACT,
  );
  const [keywords, setKeywords] = useState('');
  const onDismissModal = () => {
    setSelectedConcepts([]);
    setTriggerModal((modal) => {
      modal.visible = false;
    });
  };
  const onAddBtn = () => {
    if (
      selectedConcepts.length > 0 &&
      conceptMode === RANKING_RULE_TRIGER_MODE.CASCADE
    ) {
      setTriggerModal((modal) => {
        modal.visible = false;
        modal.cascadingConcepts = [
          ...modal.cascadingConcepts,
          selectedConcepts,
        ];
      });
    }
    if (
      selectedConcepts.length > 0 &&
      conceptMode === RANKING_RULE_TRIGER_MODE.EXACT_LOCATION
    ) {
      setTriggerModal((modal) => {
        modal.visible = false;
        modal.exactConcepts = [...modal.exactConcepts, selectedConcepts];
      });
    }
    if (keywords.length > 0) {
      const keywordList = keywords.split(',').map((s) => s.trim());
      setTriggerModal((modal) => {
        modal.visible = false;
        switch (keywordType) {
          case 'EXACT':
            modal.exactKeywords = [...modal.exactKeywords, ...keywordList];
            break;
          case 'PARTIAL':
            modal.partialKeywords = [...modal.partialKeywords, ...keywordList];
            break;
          default:
            new Error(
              `Unexpected code path - invalid keyword type ${keywordType}`,
            );
        }
      });
    }
    setSelectedConcepts([]);
    setKeywords('');
  };
  const asyncOptions = useDebouncedCallback((inputValue, callback) => {
    RecommendConceptsClient.searchConcepts(inputValue)
      .then((res) => {
        const opts = res.objects.map((c) => {
          const label =
            c.name === c.alternateName
              ? `${c.name} ${c.source.system}`
              : `${c.name}(${c.alternateName}) ${c.source.system}`;
          return {
            value: {
              id: c.id,
              name: c.name,
            },
            label,
          };
        });
        callback(opts);
      })
      .catch((err: { status: number }) => {
        console.log(err);
        callback([]);
      });
  }, 1000);

  const handleSelectChange = (
    newSelectOpts: MultiValue<{ label: string; value: any }>,
  ) => {
    const selectedConcepts = R.uniqBy(
      R.prop('id'),
      newSelectOpts.map((opt) => opt.value),
    );
    setSelectedConcepts([...selectedConcepts]);
  };

  return (
    <Modal
      onDismiss={onDismissModal}
      isOpen={triggerModal.visible}
      headerSlot={<h3 className='tw-text-lg tw-font-medium'>Rule Trigger</h3>}
      hideFade
      style={
        {
          // hack: make the gut between the header and content smaller
          '--eds-space-24': '4px',
          // hack: make the out padding of the modal smaller
          '--eds-space-32': '20px',
        } as React.CSSProperties
      }
    >
      <div
        className='select-portal-target tw-flex tw-flex-col tw-justify-center'
        style={{ width: '40rem' }}
      >
        {triggerModal.type === 'concept' && (
          <div className='tw-mb-12'>
            <RadioGroup
              className='tw-mb-4'
              id='ranking-rule-trigger-list'
              label=''
              orientation='horizontal'
              name='radio-button-group-rewrite-create'
              valueSelected={conceptMode}
              onChange={(event) => {
                const { target } = event;
                const val = (target as HTMLInputElement).value;
                setConceptMode(val);
              }}
            >
              <Radio
                label='Cascading Concepts'
                value={RANKING_RULE_TRIGER_MODE.CASCADE}
                id='ranking-rule-trigger-list-mode-1'
              />
              <Radio
                label='Exact Concepts'
                value={RANKING_RULE_TRIGER_MODE.EXACT_LOCATION}
                id='ranking-rule-trigger-list-mode-2'
              />
            </RadioGroup>

            {triggerModal.visible && (
              <AsyncSelect
                className='eds-select eds-text-field__input eds-text-field--select'
                classNamePrefix='eds-select'
                isMulti
                cacheOptions
                loadOptions={asyncOptions}
                onChange={handleSelectChange}
                menuPosition='fixed'
                menuPortalTarget={
                  document.querySelector('.select-portal-target') as HTMLElement
                }
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            )}
          </div>
        )}
        {triggerModal.type === 'keyword' && (
          <div className='tw-mb-10 tw-pb-20'>
            <RadioGroup
              className='tw-mb-2'
              id='ranking-rule-trigger-list-keyword-type'
              label='Keyword Type'
              orientation='horizontal'
              name='radio-button-group-rewrite-create'
              valueSelected={keywordType}
              onChange={(event) => {
                const { target } = event;
                const val = (target as HTMLInputElement).value;
                setKeywordType(val);
              }}
            >
              <Radio
                label='Match Exact'
                value={RANKING_RULE_TRIGER_MODE.EXACT}
                id='ranking-rule-trigger-list-keyword-type-1'
              />
              <Radio
                label='Match All'
                value={RANKING_RULE_TRIGER_MODE.PARTIAL}
                id='ranking-rule-trigger-list-keyword-type-3'
              />
            </RadioGroup>
            <TextField
              id='product-ranking-rule'
              label=''
              className='tw-w-full'
              placeholder='Enter keywords here, seperate by comma'
              value={keywords}
              onChange={(event) => {
                const { target } = event;
                const val = (target as HTMLInputElement).value;
                setKeywords(val);
              }}
            />
          </div>
        )}
        <ButtonGroup className='tw-justify-end'>
          {canWrite && (
            <Button onClick={onAddBtn} variant='primary'>
              Add
            </Button>
          )}
          <Button onClick={onDismissModal} variant='secondary'>
            Cancel
          </Button>
        </ButtonGroup>
      </div>
    </Modal>
  );
}
